import type { RouteHandle } from '~/types/route-handle';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import type { Route } from './+types/root';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  useLocation,
  data,
} from 'react-router';

import { ClerkProvider } from '@clerk/react-router';
import { rootAuthLoader } from '@clerk/react-router/ssr.server';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Modals from '~/components/modals/Modals';
import { Toaster } from '~/components/ui/sonner';
import CookieNotice from '~/components/CookieNotice';
import { PHProvider } from '~/provider';
import { createLogger } from '~/utils/logger.server';
import { handleClerkDomainRedirect, handleLoadBalancerHostHeader } from '~/utils/clerk-domain-handler.server';
import { getPostAuthPath } from '~/utils/auth.server';
import { detectLocale, localeCookie } from '~/modules/lingui/lingui.server';
import { useEffect } from 'react';
import { usePostHog } from '~/hooks/usePostHog';
import { i18n } from '~/modules/lingui/lingui';

import './tailwind.css';

// Add this at the top of the file, before any imports
declare global {
  interface Window {
    ENV: {
      CLERK_PUBLISHABLE_KEY?: string;
      SENTRY_ENABLED?: string;
      POSTHOG_API_KEY?: string;
      POSTHOG_HOST?: string;
      VIDEO_ANALYSIS_SERVICE_URL?: string;
      SOUND_ANALYSIS_SERVICE_URL?: string;
      R2_PUBLIC_URL?: string;
      POST_AUTH_PATH?: string;
    };
    __LINGUI_CATALOG__?: {
      locale: string;
      messages: Record<string, unknown>;
    };
    fbq?: (action: string, event: string, data?: Record<string, any>) => void;
    _fbq?: any;
    gtag?: (...args: any[]) => void;
    Tawk_API?: Record<string, unknown>;
    Tawk_LoadStart?: Date;
  }
}

// Create a client
const queryClient = new QueryClient();

// Safe function to get Clerk publishable key
function getClerkPublishableKey() {
  const key = process.env.CLERK_PUBLISHABLE_KEY;
  if (!key) {
    throw new Error('Missing CLERK_PUBLISHABLE_KEY environment variable');
  }
  return key;
}

export const meta: MetaFunction = ({ matches, location }) => {
  const lastMatch = matches[matches.length - 1] as { handle?: RouteHandle };
  const title = lastMatch?.handle?.title || 'Pavone - AI Job Interview Practice';
  const description = lastMatch?.handle?.description || 'Practice job interview answers with AI-powered feedback. Get real-time analysis on your speech, tone, and delivery. Try for free.';
  const canonicalUrl = `https://pavone.ai${location.pathname}`;

  return [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { property: 'og:url', content: canonicalUrl },
    { name: 'og:title', content: title },
    { name: 'og:description', content: description },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];
};

export const links = () => [
  // Favicon - PNG format preferred by Google (must be at least 48x48px)
  { rel: 'icon', type: 'image/png', href: '/images/pavone-mini-v2.png' },
  { rel: 'icon', type: 'image/x-icon', href: '/images/pavone-mini.ico' },
  // Apple touch icon for iOS and better Google indexing
  { rel: 'apple-touch-icon', href: '/images/pavone-mini-v2.png' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=TikTok+Sans:ital,wght@0,100..900;1,100..900&display=swap',
  },
];

export const loader = async (args: LoaderFunctionArgs) => {
  const logger = createLogger('Root');

  try {

    const processedRequest = handleLoadBalancerHostHeader(args.request);
    const processedArgs = { ...args, request: processedRequest };

    // Handle Load Balancer host header configuration and subdomain redirects
    // const result = handleLoadBalancerHostHeader(args.request);
    
    // // If it's a redirect response, return it immediately
    // if (result instanceof Response) {
    //   return result;
    // }
    
    // // Otherwise, it's a processed request
    // const processedArgs = { ...args, request: result };

    const postAuthPath = getPostAuthPath();

    // Detect locale from cookie or Accept-Language header
    const locale = await detectLocale(processedRequest);

    // Debug logging for post-auth path
    if (process.env.NODE_ENV === 'development') {
      console.log('[Root Loader] POST_AUTH_PATH:', postAuthPath);
      console.log('[Root Loader] NAVIGATES_AFTER_AUTH env var:', process.env.NAVIGATES_AFTER_AUTH);
      console.log('[Root Loader] Detected locale:', locale);
    }

    // Delegate to Clerk's rootAuthLoader and attach our ENV as additional data.
    return rootAuthLoader(processedArgs, async () => {
      const headers = new Headers();
      headers.append("Set-Cookie", await localeCookie.serialize(locale));

      return data({
        ENV: {
          CLERK_PUBLISHABLE_KEY: getClerkPublishableKey(),
          SENTRY_ENABLED: process.env.SENTRY_ENABLED || 'true',
          POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
          POSTHOG_HOST: process.env.POSTHOG_HOST,
          VIDEO_ANALYSIS_SERVICE_URL:
            process.env.VIDEO_ANALYSIS_SERVICE_URL || undefined,
          SOUND_ANALYSIS_SERVICE_URL:
            process.env.SOUND_ANALYSIS_SERVICE_URL || undefined,
          R2_PUBLIC_URL: process.env.R2_PUBLIC_URL || undefined,
          POST_AUTH_PATH: postAuthPath,
        },
        locale,
      }, { headers });
    });
  } catch (error) {
    if (error instanceof Response && error.status === 307) {
      return error; // Return the redirect response
    }
    logger.error('Root loader error', {
      metadata: {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      },
    });
    throw error;
  }
};

function App() {
  const loaderData = useLoaderData<typeof loader>();
  const location = useLocation();
  const { trackPageView, isLoaded } = usePostHog();

  useEffect(() => {
    // Only track when PostHog is loaded
    if (isLoaded) {
      trackPageView(location.pathname, { 
        title: document.title,
        url: window.location.href,
        search: location.search,
        hash: location.hash,
      });
    }
  }, [location.pathname, location.search, location.hash, trackPageView, isLoaded]);

  return (
    <html lang={loaderData?.locale || "en"} className="h-full" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />

        {/* Inline i18n catalog for synchronous hydration (prevents FOUC) */}
        <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html:
          `window.__LINGUI_CATALOG__=${JSON.stringify({ locale: loaderData?.locale || "en", messages: i18n.messages })}`
        }} />

        {/* Meta Pixel Code */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1144718734439730');
              fbq('track', 'PageView');
            `,
          }}
        />

        {/* Capture fbclid from Facebook ads and store as _fbc cookie */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function(){
                var m=window.location.search.match(/[?&]fbclid=([^&]*)/);
                if(m&&m[1]){
                  var d=new Date();d.setDate(d.getDate()+90);
                  document.cookie="_fbc=fb.1."+Date.now()+"."+decodeURIComponent(m[1])+";path=/;expires="+d.toUTCString()+";SameSite=Lax";
                }
              })();
            `,
          }}
        />

        {/* Google tag (gtag.js) */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-17722559182"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-17722559182');
            `,
          }}
        />

        {/* LinkedIn Insight Tag */}
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
              _linkedin_partner_id = "8970473";
              window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
              window._linkedin_data_partner_ids.push(_linkedin_partner_id);
              (function(l) {
                if (!l){
                  window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
                  window.lintrk.q=[]
                }
                var s = document.getElementsByTagName("script")[0];
                var b = document.createElement("script");
                b.type = "text/javascript";
                b.async = true;
                b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
                s.parentNode.insertBefore(b, s);
              })(window.lintrk);
            `,
          }}
        />

        {/* Structured Data for AI and Search Engines */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "PavoneAI",
              "applicationCategory": "EducationalApplication",
              "browserRequirements": "Requires JavaScript. Requires HTML5.",
              "operatingSystem": "Any (Web-based - works on Windows, macOS, Linux, iOS, Android)",
              "availableOnDevice": "Desktop, Mobile, Tablet",
              "installUrl": "https://pavone.ai/sign-up",
              "offers": {
                "@type": "Offer",
                "price": "8",
                "priceCurrency": "USD",
                "description": "Free tier available with paid plans starting at $8/month"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.8",
                "ratingCount": "2341",
                "bestRating": "5",
                "worstRating": "1"
              },
              "description": "Pavone is an AI-powered speaking practice platform that helps professionals improve their communication skills through instant feedback on speech, tone, body language, and delivery. Perfect for job interviews, sales pitches, presentations, and content creation.",
              "featureList": [
                "AI-powered speech analysis",
                "Real-time feedback on tone and pace",
                "Body language and eye contact tracking",
                "Filler word detection",
                "Practice for job interviews",
                "Sales pitch improvement",
                "Presentation coaching",
                "Content creation optimization",
                "24/7 availability",
                "Unlimited practice sessions"
              ],
              "url": "https://pavone.ai",
              "screenshot": "https://pavone.ai/images/demo.png",
              "author": {
                "@type": "Organization",
                "name": "Pavone",
                "url": "https://pavone.ai"
              },
              "potentialAction": {
                "@type": "UseAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://pavone.ai/sign-up",
                  "actionPlatform": [
                    "http://schema.org/DesktopWebPlatform",
                    "http://schema.org/MobileWebPlatform"
                  ]
                }
              }
            })
          }}
        />
      </head>
      <body className="h-full" suppressHydrationWarning>
        {/* Meta Pixel noscript fallback */}
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            src="https://www.facebook.com/tr?id=1144718734439730&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        
        {/* LinkedIn Insight Tag noscript fallback */}
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: 'none' }}
            alt=""
            src="https://px.ads.linkedin.com/collect/?pid=8970473&fmt=gif"
          />
        </noscript>
        
        <PHProvider>
          <QueryClientProvider client={queryClient}>
            <Outlet />
            <Modals />
            <Toaster />
            <CookieNotice />
            <ScrollRestoration />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.ENV = ${JSON.stringify(loaderData?.ENV || {})}`,
              }}
            />
            {/* Tawk.to Live Chat Widget */}
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
                  (function(){
                    var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
                    s1.async=true;
                    s1.src='https://embed.tawk.to/69790ca2f957841983b37896/1jg0dinem';
                    s1.charset='UTF-8';
                    s1.setAttribute('crossorigin','*');
                    s0.parentNode.insertBefore(s1,s0);
                  })();
                `,
              }}
            />
            <Scripts />
          </QueryClientProvider>
        </PHProvider>
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  // Log detailed error information for debugging
  if (typeof window === 'undefined') {
    // Server-side logging
    console.error('[SSR ErrorBoundary]', {
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name,
      } : error,
      isRouteError: isRouteErrorResponse(error),
      timestamp: new Date().toISOString(),
    });
  } else {
    // Client-side logging
    console.error('[Client ErrorBoundary]', error);
  }

  return (
    <html>
      <head>
        <title>Error!</title>
        <Meta />
        <Links />
      </head>
      <body>
        <div className="p-4">
          <h1 className="text-2xl font-bold text-red-600">
            {isRouteErrorResponse(error)
              ? `${error.status} ${error.statusText}`
              : 'Application Error'}
          </h1>
          {error instanceof Error && (
            <pre className="mt-4 p-4 bg-gray-100 rounded text-sm overflow-auto">
              {error.message}
            </pre>
          )}
        </div>
        <Scripts />
      </body>
    </html>
  );
}

// Default export receives loaderData as props for SSR compatibility
// This is required by @clerk/react-router for proper SSR hydration
export default function AppWithProviders({ loaderData }: Route.ComponentProps) {
  return (
    <ClerkProvider loaderData={loaderData}>
      <App />
    </ClerkProvider>
  );
}
